// C:\Users\server\Desktop\FRONTEND\app-user-front\src\utils\api.js
import CONFIG from '../config';

/**
 * Return API base (keeps /api if present).
 * Example: "https://demo.swapiify.com/api" or "https://demo.swapiify.com"
 */
export function getApiBase() {
  const url = CONFIG?.API_URL || "";
  return String(url).replace(/\/+$/, ""); // remove trailing slashes
}

/**
 * Return site base (strip trailing '/api' if present).
 * Example: "https://demo.swapiify.com"
 *
 * Use this for building public image/file URLs which usually live under the site root,
 * not under the API namespace.
 */
export function getSiteBase() {
  const raw = getApiBase();
  if (!raw) return "";
  return raw.replace(/\/api$/i, "");
}

/**
 * Compute a public image (or file) URL from a product object or a relative string.
 * - Accepts either an object (product) or a string path.
 * - If input is already an absolute URL (http/https) it is returned unchanged.
 * - If input is relative and starts with "api/" or "/api/", that segment is removed.
 * - Returns null when input is empty/invalid.
 *
 * Examples:
 *  computeImageUrl('uploads/products/pic.jpg') => 'https://site/uploads/products/pic.jpg'
 *  computeImageUrl({ image: 'uploads/..' }) => 'https://site/uploads/..'
 *  computeImageUrl('https://cdn.example.com/pic.jpg') => 'https://cdn.example.com/pic.jpg'
 */
export function computeImageUrl(productOrRel) {
  // accept object or string
  const relOrProduct = productOrRel ?? "";
  // if object, try common fields
  if (typeof relOrProduct === "object") {
    if (!relOrProduct) return null;
    if (relOrProduct.image_full_url) return relOrProduct.image_full_url;
    const rel = relOrProduct.image || relOrProduct.main_image || relOrProduct.image_url || "";
    return computeImageUrl(rel);
  }

  let rel = String(relOrProduct || "").trim();
  if (!rel) return null;

  // if already an absolute URL, return as-is
  if (/^https?:\/\//i.test(rel)) return rel;

  // strip leading 'api/' if present, and any leading slashes
  rel = rel.replace(/^\/?api\//i, "").replace(/^\/+/, "");

  // site base (without /api)
  const siteBase = getSiteBase();
  if (!siteBase) return `/${rel}`; // relative fallback

  return `${siteBase}/${rel}`;
}

/**
 * Simple wrapper around fetch for API calls.
 * path: string path relative to API base (e.g. '/products' or 'products/1')
 * options: fetch options (method, headers, body, etc.)
 *
 * Note: This function expects the API to return JSON (or empty body). If the server
 * returns non-JSON, apiFetch will return { raw: text }.
 */
export async function apiFetch(path, options = {}) {
  const base = getApiBase();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const fullUrl = base + cleanPath;

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {})
  };

  const response = await fetch(fullUrl, {
    ...options,
    headers
  });

  const text = await response.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    data = { raw: text };
  }

  if (!response.ok) {
    // try to surface a helpful message
    const msg = data?.message || data?.error || `API Error: ${response.status}`;
    const err = new Error(msg);
    err.response = response;
    err.data = data;
    throw err;
  }

  return data;
}
